import axios from "axios";
import authHeader from "./authHeader";
import AxiosService from "./axiosService";
import EventBus from "src/services/utils/appEventBusService";
import BusEnum from "src/enums/BusEnum";
import appNotificationService from "src/services/utils/appNotificationService";
import appConsoleService from "src/services/utils/appConsoleService";
import {useAppInboxStore} from "src/stores/appInboxStore";

// Clic sur une notification système : l'application s'ouvre sur /?notif=ID.
// Lu dès le chargement du module, avant que le routeur ne réécrive l'URL.
const openedNotificationId = new URLSearchParams(window.location.search).get('notif');

const HTML_ESCAPES = {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'};
const escapeHtml = (text) => String(text || '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);

/**
 * Boîte de réception (cloche) : chargement, lu / non lu, suppression, et réception
 * en temps réel par WebSocket (NOTIFICATION_NEW) avec toast et son.
 * Les appels passent par axios directement : AxiosService met en cache les POST.
 */
class AppInboxService {

  getStore() {
    if (!this.store) {
      this.store = useAppInboxStore()
    }
    return this.store
  }

  url(target) {
    AxiosService.setEndPoint();
    return AxiosService.ENDPOINT + target;
  }

  /** Écoute temps réel (une seule fois) puis chargement de la liste */
  async init() {
    if (!this.listening) {
      this.listening = true
      EventBus.on(BusEnum.NOTIFICATION_NEW, (data) => {
        const notification = data && data.message
        if (!notification) return
        this.getStore().prepend(notification)
        // Toast + son (si activé)
        // (le toast affiche du HTML : le texte reçu est échappé, ses retours à la ligne conservés)
        const title = escapeHtml(notification.title)
        const body = escapeHtml(notification.body).replace(/\r?\n/g, '<br>')
        appNotificationService.pushInfo(body ? `<b>${title}</b><br>${body}` : title)
      })
      // Clic sur une notification système alors que l'application était déjà ouverte :
      // le service worker la met au premier plan et demande d'afficher la notification
      navigator.serviceWorker?.addEventListener('message', (event) => {
        if (event.data?.type === 'OPEN_NOTIFICATION') this.show(event.data.id)
      })
      // Avec addEventListener (et non onmessage), les messages restent en attente sans cet appel
      navigator.serviceWorker?.startMessages?.()
    }
    await this.load()
    this.showOpenedNotification()
  }

  async load(max = 30, offset = 0) {
    try {
      const {data} = await axios.post(this.url('/appNotification/list'), {max, offset}, {headers: authHeader()});
      this.getStore().load(data);
    } catch (error) {
      appConsoleService.error('appInboxService', error);
    }
  }

  /** Application ouverte depuis une notification système (/?notif=ID) : notification affichée */
  showOpenedNotification() {
    if (!openedNotificationId || this.openedHandled) return
    this.openedHandled = true
    this.show(openedNotificationId)
  }

  /**
   * Affiche une notification en entier (fenêtre AppNotificationViewer) et la marque lue
   * @param {number|string|object} target id, ou la notification elle-même
   */
  async show(target) {
    const id = String(target && typeof target === 'object' ? target.id : target)
    const find = () => this.getStore().records.find(n => String(n.id) === id)
    let notification = find()
    // Reçue pendant que la liste était déjà chargée (application en arrière-plan) : rechargement
    if (!notification) {
      await this.load()
      notification = find()
    }
    if (!notification) return
    this.getStore().open(notification)
    if (!notification.read) this.markRead(notification.id)
  }

  async markRead(id) {
    this.getStore().markRead(id);
    try {
      await axios.post(this.url('/appNotification/markRead'), {id}, {headers: authHeader()});
    } catch (error) {
      appConsoleService.error('appInboxService', error);
    }
  }

  async markAllRead() {
    this.getStore().markAllRead();
    try {
      await axios.post(this.url('/appNotification/markAllRead'), {}, {headers: authHeader()});
    } catch (error) {
      appConsoleService.error('appInboxService', error);
    }
  }

  /** Destinataires possibles d'un message : [{id, name, image}] */
  async recipients() {
    const {data} = await axios.get(this.url('/appNotification/recipients'), {headers: authHeader()});
    return data.recipients || [];
  }

  /**
   * Message à un autre utilisateur (notification dont le titre est le nom de l'expéditeur)
   * @returns {Promise<{recipient: string, sent: number}>}
   */
  async sendMessage(userId, body) {
    const {data} = await axios.post(this.url('/appNotification/sendMessage'), {userId, body}, {headers: authHeader()});
    return data;
  }

  /** Message envoyé ou reçu, relu depuis le journal des activités : {id, sender, recipient, body, createdAt, readAt} */
  async message(id) {
    const {data} = await axios.get(this.url(`/appNotification/message/${id}`), {headers: authHeader()});
    return data;
  }

  /**
   * Envoie un message et informe l'expéditeur du résultat : reçu, ou en attente
   * dans la cloche si le destinataire n'a pas activé les notifications
   * @param {number} userId destinataire
   * @param {string} body message
   * @param {string} fallbackName nom affiché si le serveur ne le renvoie pas
   */
  async sendMessageWithFeedback(userId, body, fallbackName = '') {
    const result = await this.sendMessage(userId, body);
    const name = result.recipient || fallbackName;
    if (result.sent > 0) {
      appNotificationService.pushInfo(`Message envoyé à ${name}`);
    } else if (!result.devices) {
      appNotificationService.pushWarning(result.online
        ? `Message reçu par ${name} dans l'application, mais ${name} n'a pas activé les notifications sur ses appareils.`
        : `${name} n'a pas activé les notifications : le message l'attend dans sa cloche, à sa prochaine connexion.`);
    } else {
      appNotificationService.pushWarning(result.online
        ? `Message reçu par ${name} dans l'application, mais la notification n'a pas pu être envoyée sur ses appareils.`
        : `La notification n'a pas pu être envoyée à ${name} : le message l'attend dans sa cloche.`);
    }
    return result;
  }

  async remove(id) {
    this.getStore().remove(id);
    try {
      await axios.post(this.url('/appNotification/remove'), {id}, {headers: authHeader()});
    } catch (error) {
      appConsoleService.error('appInboxService', error);
    }
  }
}

export default new AppInboxService();
